import { css } from '@emotion/core'
import zionDebug from '@fs/zion-debug'
import { useFeatureFlags } from '@fs/zion-flags'
import { HelpTip, SocialStar } from '@fs/zion-icon'
import { i18n } from '@fs/zion-locale'
import { useQueryParams } from '@fs/zion-router'
import { getImage, useSlsStateContext } from '@fs/zion-sls'
import { Alert, Button, Card, Divider, List, ListItem, Paragraph, Separator, Skeleton, useOverlay } from '@fs/zion-ui'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import searchRecordImg from '../../images/search-record.png'
import { useFullTextStateContext, useFullTextDispatchContext } from './providers/FullTextContext'
import { useR2Highlights, useR2StateContext } from '../../providers/r2Provider/R2Context'
import { useOverlayContext } from '../../../shared/providers/overlayContextProvider/OverlayContext'
import { useSharedBrowserStateContext } from '../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import HighlightPanelOld from '../../components/generic/panels/highlightPanelOld/HighlightPanelOld'
import useStatusOverlayTrigger from '../../hooks/useStatusOverlayTrigger'
import useTreeAttachmentPrompts from '../../hooks/useTreeAttachmentPrompts'
import DownloadOverlay from './components/downloadOverlay/DownloadOverlay'
import useDownloadOverlayData from './hooks/useDownloadOverlayData'
import Transcript from './components/transcript/Transcript'
import hasMeaningfulContent from './utils/hasMeaningfulContent'
import { parseAiResponse } from './utils/parseAiResponse'
import useTranslate from '../../hooks/useTranslate'
import { getSourceLanguage } from '../../hooks/useTranslate.utils'
import {
  buildKeywordFields,
  fetchSummarization as fetchSummarizationUtil,
  getImagePermissionsBoolean,
  getDownloadPermission,
  fetchArtifact,
} from './FullText.utils'
import NoTranscriptMessage from './components/noTranscriptMessage/NoTranscriptMessage'
import FullTextToolbar from './components/fullTextToolbar/FullTextToolbar'
import FullTextActions from './components/fullTextActions/FullTextActions'
import FullTextSummarization from './components/fullTextSummarization/FullTextSummarization'
import AIPersonsExpander from './components/AiIndexing/AIPersonsExpander'
import AddNamesPanel from './components/addNamesPanel/AddNamesPanel'

const maxHeightCss = css`
  max-height: 300px;
  overflow: scroll;
`

const debug = zionDebug('rip:FullText')

const FullText = ({ fullTextAttachFlowV1IsOn = true }) => {
  const [t] = useTranslation()
  const {
    rip_sharedImageViewer_attachTreePrompt: attachTreePromptFlag,
    rip_sharedImageViewer_azure41StreamingPrompt: azure41StreamingPromptFlag,
    rip_sharedImageViewer_openAiGPT5: openAiGPT5Flag,
    rip_sharedImageViewer_autoIndexing: autoIndexingFlag,
    rip_sharedImageViewer_fullTextAttachFlow_v2: attachFlowV2,
    rip_sharedImageViewer_showTranscriptEditButton: showTranscriptEditButton,
  } = useFeatureFlags([
    'rip_sharedImageViewer_attachTreePrompt',
    'rip_sharedImageViewer_azure41StreamingPrompt',
    'rip_sharedImageViewer_openAiGPT5',
    'rip_sharedImageViewer_autoIndexing',
    'rip_sharedImageViewer_fullTextAttachFlow_v2',
    'rip_sharedImageViewer_showTranscriptEditButton',
  ])

  const downloadOverlay = useOverlay()
  const state = useSlsStateContext()
  const fullTextState = useFullTextStateContext()
  const r2State = useR2StateContext()
  const fullTextDispatch = useFullTextDispatchContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const { query } = useQueryParams()
  const { displayHighlights } = useR2Highlights()
  const { triggerStatusOverlay } = useStatusOverlayTrigger()

  const [showKeywords, setShowKeywords] = useState(true)
  const [allHighlights, setAllHighlights] = useState([])
  const [isExpanderOpen, setIsExpanderOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [summarizationData, setSummarizationData] = useState(null) // Holds the current image's transcript data
  const [cachedSummarizations, setCachedSummarizations] = useState({}) // Cache for all fetched transcripts
  const [summarization] = useState('Here is the response we get from OpenAI')
  const [artifact, setArtifact] = useState()
  const [artifactLoadErr, setArtifactLoadErr] = useState()
  const [showingTranslation, setShowingTranslation] = useState(false)
  const [summaryError, setSummaryError] = useState(false)
  const [isEditHelpDismissed, setIsEditHelpDismissed] = useState(localStorage.getItem('editHelpDismissed') === 'true')
  const [streamingContent, setStreamingContent] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [isRequestInProgress, setIsRequestInProgress] = useState(false) // State to prevent concurrent requests
  const overlay = useOverlay()
  const { selectPersonToAttachToTreeInfoSheetOverlay, inPanelStack } = useOverlayContext()

  // Allow for alternate message if transcript is not available.
  const noTranscriptMessage = !fullTextState.imagePermission
    ? t('full-text.transcription-image-permission-false', 'Transcript is not available, image restricted.')
    : t('full-text.transcription-not-available', 'Transcript is not available.')
  const shouldFetch = useRef(false) // Ref to control the fetch behavior

  const { citation, plainTextTranscript, fields, imageRecordsError } = useDownloadOverlayData(setAllHighlights)
  const handleExpanderToggle = useCallback(() => {
    setIsExpanderOpen((prev) => !prev)
    shouldFetch.current = true // Allow fetch when user toggles expander
  }, [])

  const { translationLoadErr, translations } = useTranslate({
    plainTextTranscript,
    artifact,
    showingTranslation,
    group: r2State?.groupData,
  })

  const handleEditModeToggle = useCallback(() => {
    fullTextDispatch({
      type: 'SET_TRANSCRIPT_EDIT_MODE',
      value: !fullTextState.editTranscriptMode,
    })
    if (!isEditHelpDismissed) {
      setIsEditHelpDismissed(true)
      localStorage.setItem('editHelpDismissed', 'true')
    }
  }, [fullTextState.editTranscriptMode, fullTextDispatch, isEditHelpDismissed])

  const currentImageArk = useMemo(() => state.currentImageArk, [state.currentImageArk])

  const keywords = useMemo(
    () => query.keywords?.split(',').map((keyword) => decodeURIComponent(keyword)?.toLowerCase()) || [],
    [query.keywords]
  )

  const currentImage = useMemo(() => getImage(state?.images, state?.currentImageArk) || {}, [state])

  const keywordFields = useMemo(() => {
    return buildKeywordFields(keywords, fields)
  }, [keywords, fields])

  useEffect(() => {
    setIsExpanderOpen(false)
    setSummarizationData(null) // Reset transcript data when navigating to a new image
    shouldFetch.current = false // Reset the fetch control
    setSummaryError(false) // Reset error
    setStreamingContent('') // Reset streaming content
    setIsStreaming(false) // Reset streaming state
    setIsRequestInProgress(false) // Reset request flag
  }, [state.currentImageArk])

  const fetchSummarization = useCallback(async () => {
    // Prevent multiple concurrent requests
    if (isRequestInProgress) {
      return
    }

    setIsRequestInProgress(true)

    try {
      await fetchSummarizationUtil({
        currentImageArk,
        plainTextTranscript,
        cachedSummarizations,
        setSummarizationData,
        setCachedSummarizations,
        setLoading,
        setSummaryError,
        azure41StreamingPromptFlagIsOn: azure41StreamingPromptFlag.isOn,
        openAiGPT5FlagIsOn: openAiGPT5Flag.isOn,
        parseTranscript: parseAiResponse,
        hasMeaningfulContent,
        setStreamingContent,
        setIsStreaming,
      })
    } finally {
      setIsRequestInProgress(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentImageArk, plainTextTranscript, cachedSummarizations, azure41StreamingPromptFlag.isOn, openAiGPT5Flag.isOn])

  useEffect(() => {
    if (!isExpanderOpen) return
    fetchSummarization()
  }, [isExpanderOpen, fetchSummarization])

  useEffect(() => {
    getImagePermissionsBoolean(currentImageArk, fullTextDispatch)
    getDownloadPermission(currentImageArk, fullTextDispatch)
  }, [currentImageArk, fullTextDispatch])

  useEffect(() => {
    // When https://fhjira.churchofjesuschrist.org/browse/RS-1469 is done, we can remove this call
    fetchArtifact(thumbnails, state.currentImageArk, setArtifact, setArtifactLoadErr)
  }, [thumbnails, state.currentImageArk])

  useEffect(() => {
    fullTextDispatch({
      type: 'SET_TRANSCRIPT_EDIT_MODE',
      value: false,
    })
  }, [currentImageArk, fullTextDispatch])

  const { startViewTimer } = useTreeAttachmentPrompts({
    openAttachToTree: () => selectPersonToAttachToTreeInfoSheetOverlay.open(),
  })

  useEffect(() => {
    // Only show timer prompt if v1/v2 flags are ON
    if (
      currentImage &&
      attachTreePromptFlag?.isOn &&
      query.view === 'fullText' &&
      (fullTextAttachFlowV1IsOn || attachFlowV2.isOn)
    ) {
      startViewTimer()
    }
  }, [
    currentImage,
    startViewTimer,
    attachTreePromptFlag?.isOn,
    query.view,
    fullTextAttachFlowV1IsOn,
    attachFlowV2.isOn,
  ])

  const showEditHelp = !isEditHelpDismissed

  // Determine the language for the artifact with fallback to group language or 'en'
  // Enable translation when the source language differs from the site language and there's no error
  const artifactLanguage = getSourceLanguage(artifact, r2State?.groupData)
  const enableTranslation = artifactLanguage !== i18n.language && !translationLoadErr

  debug('Render: ', {
    keywords,
    keywordFields,
    allHighlights,
    fields,
    artifactLanguage,
    groupLanguage: r2State?.groupData?.languages?.[0],
    enableTranslation,
  })

  return (
    <>
      {/* If a transcript is not available, an additional condition can be added to render the message. */}
      {!fullTextState.imagePermission && (
        <NoTranscriptMessage message={noTranscriptMessage} searchRecordImg={searchRecordImg} />
      )}
      {fullTextState.imagePermission && (
        <>
          {!fullTextAttachFlowV1IsOn && !attachFlowV2.isOn && !fullTextState.editTranscriptMode && (
            <>
              <Separator size="xs" />
              <FullTextActions showKeywords={showKeywords} setShowKeywords={setShowKeywords} />
              <Separator size="xs" />
            </>
          )}

          <Divider />
          {fullTextState.editTranscriptMode && (
            <Alert type="help" dismissable>
              <Paragraph size="sm">{t('full-text.click-to-edit', 'Click on a word to edit.')}</Paragraph>
            </Alert>
          )}
          {autoIndexingFlag.isOn && hasMeaningfulContent(plainTextTranscript) && !fullTextState.editTranscriptMode && (
            <>
              <AIPersonsExpander plainTextTranscript={plainTextTranscript} />
              <Divider />
            </>
          )}
          {hasMeaningfulContent(plainTextTranscript) && !fullTextState.editTranscriptMode && (
            <FullTextSummarization
              stateCurrentImageArk={state.currentImageArk}
              overlay={overlay}
              currentImageArk={currentImageArk}
              isExpanderOpen={isExpanderOpen}
              handleExpanderToggle={handleExpanderToggle}
              summarization={summarization}
              summarizationData={summarizationData}
              summaryError={summaryError}
              fetchSummarization={fetchSummarization}
              loading={loading}
              streamingContent={streamingContent}
              isStreaming={isStreaming}
            />
          )}
          <Separator size="sm" />
          <FullTextToolbar
            plainTextTranscript={plainTextTranscript}
            downloadOverlay={downloadOverlay}
            fullTextDispatch={fullTextDispatch}
            fullTextState={fullTextState}
            handleEditModeToggle={handleEditModeToggle}
            showKeywords={showKeywords}
            setShowKeywords={setShowKeywords}
          />
          <Separator size="xs" />
          {/* Temporarily hiding edit button https://icseng.atlassian.net/browse/RIP-4759 */}
          {showEditHelp && showTranscriptEditButton.isOn && (
            <>
              <Alert type="help" dismissable>
                {t('full-text.edit-help', 'Click the pencil above to make edits to the transcript.')}
              </Alert>
              <Separator size="xs" />
            </>
          )}

          <Button
            Icon={SocialStar}
            onClick={() => setShowingTranslation((prev) => !prev)}
            data-testid="full-text-translate"
            linkName="full-text-show-translation"
            disabled={!enableTranslation}
          >
            {showingTranslation
              ? t('full-text.hide-translation', 'Hide Translation')
              : t('full-text.show-translation', 'Show Translation')}
          </Button>

          {(artifactLoadErr || translationLoadErr) && (
            <>
              <Separator size="xs" />
              <Alert type="error" dismissable>
                {t('full-text.translation-error', 'Unable to translate transcript. Reload page.')}
              </Alert>
              <Separator size="xs" />
            </>
          )}

          {showingTranslation && !translationLoadErr && (
            <>
              <List>
                <ListItem
                  primaryText={t('full-text.translation-tip', 'Translation is in site language')}
                  startElement={<ListItem.Icon Icon={HelpTip} />}
                />
              </List>
              {t('full-text.translation-ai-generated', 'AI-generated from the transcription below.')}
              <Separator size="xs" />
              <div css={maxHeightCss}>
                <Card edges="border">
                  {!translations[state.currentImageArk] && <Skeleton.Paragraph />}
                  {translations[state.currentImageArk]}
                </Card>
              </div>
              <Separator size="xs" />
            </>
          )}
          {imageRecordsError ? (
            <Alert type="error">
              {t('full-text.transcript-text-error', 'Error loading transcript text. Please try again later.')}
            </Alert>
          ) : (
            <Transcript
              fields={fields}
              allHighlights={allHighlights}
              showKeywords={showKeywords}
              keywordFields={keywordFields}
              selectPersonToAttachToTreeInfoSheetOverlay={selectPersonToAttachToTreeInfoSheetOverlay}
            />
          )}

          <DownloadOverlay
            downloadOverlay={downloadOverlay}
            displayHighlights={displayHighlights}
            triggerStatusOverlay={triggerStatusOverlay}
            citation={citation}
            plainTextTranscript={plainTextTranscript}
            selectPersonToAttachToTreeInfoSheetOverlay={selectPersonToAttachToTreeInfoSheetOverlay}
            query={query}
          />

          {inPanelStack('addNamesOverlay') && <AddNamesPanel />}

          {inPanelStack('highlightsPanelOld') && <HighlightPanelOld />}
        </>
      )}
    </>
  )
}

export default FullText
